import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQueueItems, useUpdateQueueItem, useDeleteQueueItem, QUEUE_STATUSES, QUEUE_REASONS } from '../../hooks/useQueueItems'
import type { QueueItem } from '../../lib/database.types'
import { Skeleton } from '../../components/ui'

const STATUS_LABELS: Record<QueueItem['status'], string> = {
  idea: 'Idea',
  planned: 'Planned',
  printing: 'Printing',
  done: 'Done',
  dropped: 'Dropped',
}

const STATUS_COLOR: Record<QueueItem['status'], string> = {
  idea: 'var(--color-text-muted)',
  planned: 'var(--color-primary)',
  printing: 'var(--color-warning)',
  done: 'var(--color-success)',
  dropped: 'var(--color-danger)',
}

const PRIORITY_COLOR: Record<QueueItem['priority'], string> = {
  low: 'var(--color-text-faint)',
  med: 'var(--color-warning)',
  high: 'var(--color-danger)',
}

export default function QueueList() {
  const { data: items, isLoading } = useQueueItems()
  const updateItem = useUpdateQueueItem()
  const deleteItem = useDeleteQueueItem()
  const [reasonFilter, setReasonFilter] = useState<string>('')

  if (isLoading) return (
    <div>
      {Array.from({ length: 5 }, (_, i) => (
        <Skeleton key={i} height={60} style={{ marginBottom: 8 }} />
      ))}
    </div>
  )

  const all = items ?? []
  const filtered = reasonFilter ? all.filter((i) => i.reason === reasonFilter) : all

  const grouped = groupByStatus(filtered)

  const activeStatuses = QUEUE_STATUSES.filter(
    (s) => (grouped[s] ?? []).length > 0,
  )

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-4)' }}>
        <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
          <button
            onClick={() => setReasonFilter('')}
            style={{
              padding: '4px 12px',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-xs)',
              fontWeight: 600,
              background: !reasonFilter ? 'var(--color-primary)' : 'var(--color-surface)',
              color: !reasonFilter ? 'white' : 'var(--color-text-muted)',
              border: '1px solid var(--color-border)',
            }}
          >
            All
          </button>
          {QUEUE_REASONS.map((r) => (
            <button
              key={r}
              onClick={() => setReasonFilter(r === reasonFilter ? '' : r)}
              style={{
                padding: '4px 12px',
                borderRadius: 'var(--radius-md)',
                fontSize: 'var(--font-size-xs)',
                fontWeight: 600,
                background: reasonFilter === r ? 'var(--color-primary)' : 'var(--color-surface)',
                color: reasonFilter === r ? 'white' : 'var(--color-text-muted)',
                border: '1px solid var(--color-border)',
              }}
            >
              {r}
            </button>
          ))}
        </div>
        <Link
          to="/prints/queue/new"
          style={{
            padding: 'var(--space-2) var(--space-4)',
            background: 'var(--color-primary)',
            color: 'white',
            borderRadius: 'var(--radius-md)',
            fontWeight: 600,
            fontSize: 'var(--font-size-sm)',
            textDecoration: 'none',
            whiteSpace: 'nowrap',
          }}
        >
          + Add item
        </Link>
      </div>

      {all.length === 0 ? (
        <div style={{ padding: 'var(--space-8)', textAlign: 'center', background: 'var(--color-surface)', borderRadius: 'var(--radius-lg)', color: 'var(--color-text-muted)' }}>
          Queue is empty — tap &quot;Add item&quot; to start planning your next print.
        </div>
      ) : filtered.length === 0 ? (
        <div style={{ padding: 'var(--space-6)', textAlign: 'center', background: 'var(--color-surface)', borderRadius: 'var(--radius-lg)', color: 'var(--color-text-muted)' }}>
          No items match this filter.
        </div>
      ) : (
        <div
          style={{
            maxHeight: '70vh',
            overflowY: 'auto',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-lg)',
            padding: 'var(--space-3)',
            background: 'var(--color-bg)',
            WebkitOverflowScrolling: 'touch',
          }}
        >
          {activeStatuses.map((status) => {
            const list = grouped[status] ?? []
            return (
              <div key={status} style={{ marginBottom: 'var(--space-6)' }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-2)',
                  padding: 'var(--space-2) var(--space-3)',
                  background: 'var(--color-surface-2)',
                  borderRadius: 'var(--radius-md)',
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 600,
                  marginBottom: 'var(--space-2)',
                }}>
                  <span style={{ color: STATUS_COLOR[status], fontSize: '0.7rem' }}>●</span>
                  <span>{STATUS_LABELS[status]}</span>
                  <span style={{ color: 'var(--color-text-faint)', fontWeight: 400 }}>{list.length}</span>
                </div>
                <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                  {list.map((item) => (
                    <li key={item.id} style={{
                      background: 'var(--color-surface)',
                      padding: 'var(--space-3)',
                      borderRadius: 'var(--radius-md)',
                      marginBottom: 'var(--space-2)',
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', flexWrap: 'wrap', marginBottom: 'var(--space-1)' }}>
                            <span style={{ fontWeight: 600 }}>{item.title}</span>
                            <span style={{
                              fontSize: 'var(--font-size-xs)',
                              padding: '1px 6px',
                              borderRadius: 'var(--radius-sm)',
                              background: PRIORITY_COLOR[item.priority],
                              color: item.priority === 'low' ? 'var(--color-text-muted)' : 'white',
                              fontWeight: 600,
                            }}>
                              {item.priority}
                            </span>
                            {item.reason && (
                              <span style={{
                                fontSize: 'var(--font-size-xs)',
                                padding: '1px 6px',
                                borderRadius: 'var(--radius-sm)',
                                background: 'var(--color-surface-2)',
                                color: 'var(--color-text-muted)',
                              }}>
                                {item.reason}
                              </span>
                            )}
                          </div>
                          <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-faint)' }}>
                            {item.customer_name && `${item.customer_name} · `}
                            {item.due_date && `Due ${item.due_date} · `}
                            {item.estimated_time_hours != null && `${item.estimated_time_hours}h · `}
                            {item.estimated_filament_g != null && `${item.estimated_filament_g}g`}
                          </div>
                          {item.notes && (
                            <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', marginTop: 'var(--space-1)' }}>
                              {item.notes}
                            </div>
                          )}
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 'var(--space-1)', marginLeft: 'var(--space-3)' }}>
                          {status !== 'done' && status !== 'dropped' && (
                            <Link
                              to={`/prints/new?queueId=${item.id}&title=${encodeURIComponent(item.title)}`}
                              style={{ color: 'var(--color-primary)', fontSize: 'var(--font-size-xs)', fontWeight: 600, whiteSpace: 'nowrap' }}
                            >
                              Print →
                            </Link>
                          )}
                          <Link
                            to={`/prints/queue/${item.id}/edit`}
                            style={{ color: 'var(--color-primary)', fontSize: 'var(--font-size-xs)', fontWeight: 600 }}
                          >
                            Edit
                          </Link>
                          {status !== 'done' && (
                            <button
                              onClick={() => updateItem.mutate({ id: item.id, status: 'done' })}
                              style={{ color: 'var(--color-success)', fontSize: 'var(--font-size-xs)', fontWeight: 600 }}
                            >
                              Done
                            </button>
                          )}
                          {status !== 'dropped' && status !== 'done' && (
                            <button
                              onClick={() => updateItem.mutate({ id: item.id, status: 'dropped' })}
                              style={{ color: 'var(--color-text-faint)', fontSize: 'var(--font-size-xs)' }}
                            >
                              Drop
                            </button>
                          )}
                          <button
                            onClick={() => {
                              if (window.confirm('Delete this queue item?')) deleteItem.mutate(item.id)
                            }}
                            style={{ color: 'var(--color-danger)', fontSize: 'var(--font-size-xs)' }}
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

function groupByStatus(items: QueueItem[]): Record<QueueItem['status'], QueueItem[]> {
  const groups = {} as Record<QueueItem['status'], QueueItem[]>
  for (const item of items) {
    if (!groups[item.status]) groups[item.status] = []
    groups[item.status].push(item)
  }
  return groups
}
